import { marker as i18n } from '@biesbjerg/ngx-translate-extract-marker';
import type { TranslateService } from '@ngx-translate/core';
import type { CountryCode } from 'libphonenumber-js';

import type { EnvironmentService } from 'src/services/environment/environment.service';
import type { GeoService } from 'src/services/geo/geo.service';
import type { ICheckboxesGdprTitleStrings, ICheckboxGdprStrings } from 'src/shared/constants/strings/gdpr-strings';
import {
    getStringsForEmailTrackingConsentCheckbox,
    getStringsForGdprConsentCheckboxes,
    getStringsForGdprEmailCheckbox,
    getStringsForGdprPhoneCheckbox,
    getStringsForGdprTitleCheckboxes,
} from 'src/shared/constants/strings/gdpr-strings';
import type {
    IContactSalesErrorList,
    IContactSalesFormOptions,
    IContactSalesServerError,
    IFormVisualSettings,
} from 'src/shared/types/contact-sales-form';
import type { EProductType } from 'src/shared/types/product-type';

/*
 *   What's going on here:
 *   1. Getting translations and list of error for Contact sales form
 *   2. Combine form options from front end
 *   3. Create object of default frontend params
 * */

export function contactSalesFormDefaultOptionsFactory(
    geoService: GeoService,
    translateService: TranslateService,
    environmentService: EnvironmentService,
    productType: EProductType
): IContactSalesFormOptions {
    const checkboxesTitleGdprStrings: ICheckboxesGdprTitleStrings = getStringsForGdprTitleCheckboxes(productType);
    const checkboxesConsentGdprStrings = getStringsForGdprConsentCheckboxes(productType);
    const checkboxGdprEmailStrings: ICheckboxGdprStrings = getStringsForGdprEmailCheckbox(productType);
    const checkboxGdprPhoneStrings: ICheckboxGdprStrings = getStringsForGdprPhoneCheckbox(productType);
    const checkboxEmailTrackingConsentStrings: ICheckboxGdprStrings =
        getStringsForEmailTrackingConsentCheckbox(productType);

    const getContactSalesFormDefaultOptions = () => ({
        errorList: {
            inputs: {
                fullName: {
                    errorRequired: i18n('Please enter your full name'),
                },
                email: {
                    errorRequired: i18n('Please enter your business email'),
                    errorWrongFormat: i18n('Please enter the valid email'),
                    errorBusinessEmail: i18n('Please enter your business email'),
                },
                phone: {
                    errorRequired: i18n('Please enter your phone'),
                    errorWrongFormat: i18n('Please enter the valid phone'),
                },
                department: {
                    errorRequired: i18n('Please enter your department'),
                },
                jobLevelText: {
                    errorRequired: i18n('Please enter your job title'),
                },
                privacy: {
                    errorRequired: checkboxGdprEmailStrings.errorMessage,
                },
                privacyPhone: {
                    errorRequired: checkboxGdprPhoneStrings.errorMessage,
                },
            },
            server: {
                400: i18n("Server error. We're really sorry. Wait a few minutes and try again."),
                417: i18n('Something wrong with sending data'),
                common: i18n('Server error. We are really sorry. Wait a few minutes and try again.'),
            },
        },
        formVisualSettings: {
            inputs: {
                fullName: {
                    placeholder: i18n('Full name'),
                    label: i18n('Full name'),
                },
                email: {
                    placeholder: i18n('name@company.com'),
                    label: i18n('Email'),
                },
                phone: {
                    placeholder: i18n('Phone'),
                    label: i18n('Phone'),
                    errorRequired: i18n('Please enter your phone'),
                },
                department: {
                    label: i18n('What department do you work in?'),
                },
                jobLevelText: {
                    placeholder: '',
                    label: i18n('What is your job title?'),
                },
                marketingNotes: {
                    placeholder: '',
                    label: i18n('How did you hear about us?'),
                },
            },
            buttonSendText: i18n('Get in touch'),
            checkboxesTitle: checkboxesTitleGdprStrings.mainMessage,
            checkboxesConsentText: checkboxesConsentGdprStrings.mainMessage,
            checkboxText: checkboxGdprEmailStrings.mainMessage,
            checkboxPhoneText: checkboxGdprPhoneStrings.mainMessage,
            checkboxEmailTrackingText: checkboxEmailTrackingConsentStrings.mainMessage,
        },
    });

    const contactSalesFormDefaultOptions = getContactSalesFormDefaultOptions();
    const errorList = contactSalesFormDefaultOptions.errorList.inputs;
    const serverErrorList = contactSalesFormDefaultOptions.errorList.server;
    const formVisualSettings = contactSalesFormDefaultOptions.formVisualSettings.inputs;
    const defaultButtonSendText = translateService.instant(
        contactSalesFormDefaultOptions.formVisualSettings.buttonSendText
    );

    const errorListDefault: IContactSalesErrorList = {
        inputs: Object.keys(errorList).reduce((object, inputName) => {
            object[inputName] = Object.keys(errorList[inputName]).reduce((inputObject, type) => {
                inputObject[type] = translateService.instant(errorList[inputName][type]);
                return inputObject;
            }, {});
            return object;
        }, {}),
        server: Object.keys(serverErrorList).reduce((acc, key) => {
            acc[key] = translateService.instant(serverErrorList[key]);
            return acc;
        }, {}) as IContactSalesServerError,
    };

    const formVisualSettingsDefault: IFormVisualSettings = {
        inputs: Object.keys(formVisualSettings).reduce((object, inputName) => {
            object[inputName] = Object.keys(formVisualSettings[inputName]).reduce((inputObject, type) => {
                const key = formVisualSettings[inputName][type];
                inputObject[type] = key ? translateService.instant(key) : '';
                return inputObject;
            }, {});
            return object;
        }, {}),
        buttonSendText: defaultButtonSendText,
        checkboxesTitle: translateService.instant(contactSalesFormDefaultOptions.formVisualSettings.checkboxesTitle),
        checkboxesConsentText: translateService.instant(
            contactSalesFormDefaultOptions.formVisualSettings.checkboxesConsentText
        ),
        checkboxEmailText: translateService.instant(contactSalesFormDefaultOptions.formVisualSettings.checkboxText),
        checkboxPhoneText: translateService.instant(
            contactSalesFormDefaultOptions.formVisualSettings.checkboxPhoneText
        ),
        checkboxEmailTrackingText: translateService.instant(
            contactSalesFormDefaultOptions.formVisualSettings.checkboxEmailTrackingText
        ),
        countryCode: geoService.getCountryCodeFromCookie() as CountryCode,
    };

    return {
        environment: environmentService.environment.origin,
        errorList: errorListDefault,
        formVisualSettings: formVisualSettingsDefault,
        businessValidation: false,
    };
}
